import React, { useState } from 'react';
import {
    Node,
    NodeViewProps,
    mergeAttributes
} from '@tiptap/core';
import {
    NodeViewContent,
    NodeViewWrapper,
    ReactNodeViewRenderer
} from '@tiptap/react';
import {
    ButtonProps,
    FeedbackCard,
    MultiSelect,
    ProductCard,
    Select,
    Selection,
} from '../components';

// Feedback Component
const FeedbackComponent: React.FC<NodeViewProps> = (props) => {
    const { node, extension } = props;
    const { sendMessage, isActionable } = extension.options;
    const { header, intent } = node.attrs;

    const handleThumbsUpClick = () => {
        sendMessage(`THUMBS_UP: ${intent}`);
    };

    const handleThumbsDownClick = () => {
        sendMessage(`THUMBS_DOWN: ${intent}`);
    };

    return (
        <NodeViewWrapper className='feedback-component'>
            <FeedbackCard
                header={header as string}
                thumbsUpClick={handleThumbsUpClick}
                thumbsDownClick={handleThumbsDownClick}
                disabled={!isActionable}
            />
        </NodeViewWrapper>
    );
};

// Feedback Node
type FeedbackTypeOptions = {
    sendMessage: (text: string) => Promise<void>;
    isActionable: boolean;
};
const FeedbackNode = Node.create<FeedbackTypeOptions>({
    name: 'feedback',
    group: 'block',
    content: 'inline*',
    parseHTML () {
        return [{
            tag: 'feedback',
            getAttrs: node => {
                return {
                    header: node.textContent,
                    intent: node.getAttribute('intent') || 'default-intent',
                };
            },
        }];
    },
    renderHTML ({ HTMLAttributes }) {
        return ['feedback', mergeAttributes(HTMLAttributes), 0];
    },
    addAttributes () {
        return {
            header: '',
            intent: {
                default: 'default-intent',
            },
        };
    },
    addNodeView () {
        return ReactNodeViewRenderer(FeedbackComponent);
    },
});

const MultiSelectComponent: React.FC<any> = (props) => {
    const { node, extension } = props;
    const { sendMessage, isActionable, selectedOption } = extension.options;

    // Parse the default selections from selectedOption.
    // selectedOption is expected to be a JSON string mapping item values to {selected, rating}.
    // If parsing fails or nothing is provided, we fall back to an empty object.
    let selectedOptionsMap: Record<string, Selection> = {};
    if (selectedOption) {
        try {
            const decodedSelection = selectedOption.replace(/&quot;/g, '"');
            selectedOptionsMap = JSON.parse(decodedSelection);
        } catch (e) {
            console.error('Error parsing selectedOption:', e);
            selectedOptionsMap = {};
        }
    }

    const items = Array.from(node.content.content).map((optionNode: any) => {
        const value = optionNode.attrs?.value || optionNode.textContent;
        // Determine selected and rating by checking the option's attribute and then the default mapping.
        const selectedOption = selectedOptionsMap[value] || {};
        return {
            label: optionNode.textContent,
            value: value,
            variant: optionNode.attrs?.variant || 'checkbox',
            selected: selectedOption.selected || false,
            rating: selectedOption.rating,
            showRating: optionNode.attrs?.showRating,
            ratingCount: optionNode.attrs?.ratingCount ? Number(optionNode.attrs.ratingCount) : 5,
        };
    });

    // When the user submits, send the selections as JSON string.
    const handleSubmit = (values: Record<string, Selection>) => {
        sendMessage(JSON.stringify(values));
    };

    return (
        <NodeViewWrapper className="multiselect-node">
            <MultiSelect
                title={node.attrs.title}
                subtitle={node.attrs.subtitle}
                submitLabel={node.attrs.submitLabel}
                items={items}
                disabled={!isActionable}
                feedback={true}
                onSubmitClick={handleSubmit}
            />
        </NodeViewWrapper>
    );
};

const MultiSelectNode = Node.create<SelectNodeOptions>({
    name: 'multiselect',
    group: 'block',
    content: 'option+',
    parseHTML () {
        return [
            {
                tag: 'multiselect',
                getAttrs: (dom: HTMLElement) => ({
                    title: dom.getAttribute('title') || '',
                    subtitle: dom.getAttribute('subtitle') || '',
                    submitLabel: dom.getAttribute('submitLabel') || 'Submit',
                }),
            },
        ];
    },
    renderHTML ({ HTMLAttributes }) {
        return ['multiselect', mergeAttributes(HTMLAttributes), 0];
    },
    addAttributes () {
        return {
            title: { default: '' },
            subtitle: { default: '' },
            submitLabel: { default: 'Submit' },
        };
    },
    addNodeView () {
        return ReactNodeViewRenderer(MultiSelectComponent);
    },
});

const ProductComponent: React.FC<NodeViewProps> = ({ node }) => {
    const { title, desc, slug, price, markdownPrice, imageUrl, chatBotSessionId } = node.attrs as {
        title: string;
        desc: string;
        slug: string;
        price: string;
        markdownPrice: string;
        imageUrl: string;
        chatBotSessionId: string;
    };
    // !title = disabled (provided slug does not have associated product details)
    return (
        <NodeViewWrapper className='product-component'>
            <ProductCard
                title={title || 'Product Details Unavailable'}
                subtext={desc}
                price={price}
                markdownPrice={markdownPrice}
                imageUrl={imageUrl}
                onClick={() => { title ? window.open(`https://ring.com/products/${slug}?chatBotSessionId=${chatBotSessionId}`, '_blank') : undefined }}
                disabled={!title}
            />
        </NodeViewWrapper>
    );
};

const ProductNode = Node.create({
    name: 'product',
    group: 'block',
    content: 'inline*',
    addAttributes () {
        return {
            title: { default: null },
            desc: { default: null },
            slug: { default: null },
            price: { default: null },
            markdownPrice: { default: null },
            imageUrl: { default: null },
            chatBotSessionId: { default: null },
        };
    },
    parseHTML () {
        return [{
            tag: 'product',
            getAttrs: node => {
                const element = node as HTMLElement;
                return {
                    title: element.getAttribute('title'),
                    desc: element.getAttribute('desc'),
                    slug: element.getAttribute('slug'),
                    price: element.getAttribute('price'),
                    markdownPrice: element.getAttribute('markdown-price'),
                    imageUrl: element.getAttribute('image-url'),
                    chatBotSessionId: element.getAttribute('chatbot-session-id'),
                };
            },
        }];
    },
    renderHTML ({ HTMLAttributes }) {
        return ['product', mergeAttributes(HTMLAttributes), 0];
    },
    addNodeView () {
        return ReactNodeViewRenderer(ProductComponent);
    },
});

const SelectComponent: React.FC<NodeViewProps> = (props) => {
    const { extension, node } = props;
    const { sendMessage, isActionable, selectedOption } = extension.options;

    const items: ButtonProps[] = Array.from((node.content as any).content).map((item: any) => {
        const value = item.attrs?.value || item.textContent;
        return {
            label: item.textContent,
            value: value,
            selected: value === selectedOption,
            disabled: !isActionable,
        };
    });

    const handleSelect = (value: string) => {
        sendMessage(value);
    };

    return (
        <NodeViewWrapper className='select-component'>
            <Select
                alignment='left'
                feedback
                orientation='vertical'
                items={items.map(item => ({
                    ...item,
                    onClick: () => handleSelect(item.value),
                }))}
            />
        </NodeViewWrapper>
    );
};

type SelectNodeOptions = {
    sendMessage: (text: string) => Promise<void>;
    isActionable: boolean;
    selectedOption: string;
};
const SelectNode = Node.create<SelectNodeOptions>({
    name: 'select',
    group: 'block',
    content: 'option+',
    parseHTML () {
        return [{ tag: 'select' }];
    },
    renderHTML ({ HTMLAttributes }) {
        return ['select', mergeAttributes(HTMLAttributes), 0];
    },
    addNodeView () {
        return ReactNodeViewRenderer(SelectComponent);
    },
});

const OptionComponent = (props: NodeViewProps) => (
    <NodeViewWrapper>
        <NodeViewContent />
    </NodeViewWrapper>
);

const OptionNode = Node.create({
    name: 'option',
    group: 'block',
    content: 'inline*',
    addAttributes () {
        return {
            value: {
                default: null,
                parseHTML: element => element.getAttribute('value'),
            },
            variant: {
                default: 'checkbox',
                parseHTML: element => element.getAttribute('variant'),
            },
            showRating: {
                default: true,
                parseHTML: element => element.getAttribute('showRating') === 'false' ? false : true,
            },
            ratingCount: {
                default: 5,
                parseHTML: element => {
                    const count = element.getAttribute('ratingCount');
                    return count ? Number(count) : 5;
                }
            },
        };
    },
    parseHTML () {
        return [{
            tag: 'option',
            getAttrs: node => ({
                value: node.getAttribute('value'),
                variant: node.getAttribute('variant') || 'checkbox',
                showRating: node.getAttribute('showRating') || 'true',
                ratingCount: node.getAttribute('ratingCount') || '5',
            }),
        }];
    },
    renderHTML ({ HTMLAttributes }) {
        return ['option', mergeAttributes(HTMLAttributes), 0];
    },
    addNodeView () {
        return ReactNodeViewRenderer(OptionComponent);
    },
});

const RatingComponent = (props: NodeViewProps) => {
    const { node, extension } = props;
    const { sendMessage, isActionable, selectedOption } = extension.options;
    const { header } = node.attrs;

    const [rating, setRating] = useState<number | null>(selectedOption);

    const ratingClick = (value: number) => {
        setRating(value);
        sendMessage(`${value}`);
    };

    return (
        <NodeViewWrapper className='rating-component'>
            <Select
                alignment='left'
                feedback
                rating
                orientation='horizontal'
                header={header as string}
                onRatingClick={ratingClick}
                disabled={!isActionable}
                selectedOption={rating}
            />
        </NodeViewWrapper>
    );
};

type RatingNodeOptions = {
    sendMessage: (text: string) => Promise<void>;
    isActionable: boolean;
    selectedOption: string;
};
const RatingNode = Node.create<RatingNodeOptions>({
    name: 'rating',
    group: 'block',
    content: 'inline*',
    parseHTML () {
        return [{
            tag: 'rating',
            getAttrs: node => {
                return {
                    header: node.textContent,
                };
            },
        }];
    },
    renderHTML ({ HTMLAttributes }) {
        return ['rating', mergeAttributes(HTMLAttributes), 0];
    },
    addAttributes () {
        return {
            header: '',
        };
    },
    addNodeView () {
        return ReactNodeViewRenderer(RatingComponent);
    },
});

export {
    FeedbackNode,
    MultiSelectNode,
    OptionNode,
    ProductNode,
    RatingNode,
    SelectNode,
};
